import { openDB, type DBSchema, type IDBPDatabase } from "idb";
import { v7 as uuidv7 } from "uuid";

/**
 * Offline outbox (phase 4). A submission that cannot reach the API is stored here,
 * on the device, together with the compressed photos it needs, and replayed later
 * by lib/sync.ts. The item id doubles as the Idempotency-Key, so a replay that
 * already reached the server can never create a second checklist.
 */
export type OutboxKind = "checklist" | "bag-checklist";

export interface OutboxItem {
  /** UUIDv7 generated on the device; also the Idempotency-Key and (for checklists) the record id. */
  id: string;
  kind: OutboxKind;
  /** API path to POST to, e.g. /api/v1/checklists */
  path: string;
  body: Record<string, unknown>;
  /** Ids of photos kept in the `blobs` store (prefixed with LOCAL_PREFIX). */
  localFileIds: string[];
  /** local id -> verified server file id, filled in as uploads succeed (survives partial replays). */
  uploaded: Record<string, string>;
  /** Short human label for the banner, e.g. "AMB-12 · 2026-09-06". */
  summary: string;
  createdAt: number;
  attempts: number;
  lastError?: string;
  status: "pending" | "failed";
}

export interface LocalBlob {
  id: string;
  blob: Blob;
  mimeType: string;
  createdAt: number;
}

interface OutboxDB extends DBSchema {
  outbox: { key: string; value: OutboxItem; indexes: { byCreated: number } };
  blobs: { key: string; value: LocalBlob };
}

export const LOCAL_PREFIX = "local:";
export const isLocalFileId = (id: string | undefined | null): id is string => typeof id === "string" && id.startsWith(LOCAL_PREFIX);

let dbPromise: Promise<IDBPDatabase<OutboxDB>> | undefined;
function db() {
  dbPromise ??= openDB<OutboxDB>("ambulance-outbox", 1, {
    upgrade(d) {
      const o = d.createObjectStore("outbox", { keyPath: "id" });
      o.createIndex("byCreated", "createdAt");
      d.createObjectStore("blobs", { keyPath: "id" });
    },
  });
  return dbPromise;
}

const listeners = new Set<() => void>();
/** Subscribe to outbox changes (enqueue, update, remove). Returns the unsubscribe function. */
export function onOutboxChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}
function notify() {
  for (const l of listeners) l();
}

export async function enqueue(item: Pick<OutboxItem, "id" | "kind" | "path" | "body" | "localFileIds" | "summary">): Promise<OutboxItem> {
  const full: OutboxItem = { ...item, uploaded: {}, createdAt: Date.now(), attempts: 0, status: "pending" };
  await (await db()).put("outbox", full);
  notify();
  return full;
}

export async function listOutbox(): Promise<OutboxItem[]> {
  return (await db()).getAllFromIndex("outbox", "byCreated");
}

export async function getOutbox(id: string): Promise<OutboxItem | undefined> {
  return (await db()).get("outbox", id);
}

export async function updateOutbox(id: string, patch: Partial<OutboxItem>): Promise<void> {
  const d = await db();
  const cur = await d.get("outbox", id);
  if (!cur) return;
  await d.put("outbox", { ...cur, ...patch });
  notify();
}

/** Remove a submission and the photos only it referenced. */
export async function removeOutbox(id: string): Promise<void> {
  const d = await db();
  const cur = await d.get("outbox", id);
  await d.delete("outbox", id);
  if (cur) for (const f of cur.localFileIds) await d.delete("blobs", f);
  notify();
}

/** Keep a compressed photo on the device; returns its local id (use it where a fileId is expected). */
export async function saveLocalBlob(blob: Blob): Promise<string> {
  const id = `${LOCAL_PREFIX}${uuidv7()}`;
  await (await db()).put("blobs", { id, blob, mimeType: blob.type, createdAt: Date.now() });
  return id;
}

export async function getLocalBlob(id: string): Promise<LocalBlob | undefined> {
  return (await db()).get("blobs", id);
}

export async function deleteLocalBlob(id: string): Promise<void> {
  await (await db()).delete("blobs", id);
}

/** True when the failure means "no connection right now" rather than "the server said no". */
export function isNetworkError(e: unknown): boolean {
  if (typeof navigator !== "undefined" && navigator.onLine === false) return true;
  if (e instanceof TypeError) return true; // fetch(): "Failed to fetch" / "Load failed"
  const name = (e as { name?: string } | undefined)?.name;
  return name === "NetworkError" || name === "AbortError";
}
